/**
 * Lets open tabs share the key that opens this person's data, so a new tab
 * doesn't ask for the password while another one is already unlocked.
 *
 * The key only ever lives in each tab's sessionStorage (lib/crypto.js). A tab
 * without it asks the others over a BroadcastChannel — same origin, same
 * browser profile, in memory, never written anywhere — and an unlocked tab
 * signed in as the same person answers with its own key (never a shared
 * account's owner key it has stepped into; lib/sharedAccess.js). That's no
 * wider than sessionStorage itself: any script that could listen here could
 * read sessionStorage already. Private windows are a separate partition and
 * never see normal tabs, and nothing survives the browser closing.
 *
 * Signing out clears the key in every tab (`announceSignedOut`), a quick
 * exit sends every tab somewhere ordinary (`announceQuickExit`,
 * lib/quickExit.js), and a tab that unlocks tells the others
 * (`announceUnlocked`), so a tab waiting on the password prompt picks the
 * key up instead.
 */
const CHANNEL = 'pd-key-share';
const OWN_PRIVATE_KEY_BACKUP = 'own_private_key_pem'; // lib/sharedAccess.js

let channel = null;
const waiting = new Map();
const unlockedListeners = new Set();
let onSignedOut = null;
let onQuickExit = null;

const subjectOf = (token) => {
  try {
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.sub || payload.user_id || null;
  } catch {
    return null;
  }
};

// Who this tab is signed in as, from the token's subject — only a tab signed
// in as the same person answers.
let knownAs = null;
const signedInAs = () => {
  let token = null;
  try { token = localStorage.getItem('auth_token'); } catch { /* blocked */ }
  const who = subjectOf(token);
  if (who) knownAs = who;
  return who;
};

// The token lives in localStorage, which every tab shares, and the tab
// signing out removes it the moment it has posted — before the others get
// the message. So "signed out" and "quick exit" are checked against who this
// tab was last signed in as, not who the (already emptied) storage says.
const lastSignedInAs = () => signedInAs() || knownAs;

const ownKey = () => {
  try {
    return sessionStorage.getItem(OWN_PRIVATE_KEY_BACKUP) || sessionStorage.getItem('private_key');
  } catch {
    return null;
  }
};

const onMessage = (event) => {
  const m = event.data || {};
  const me = signedInAs();
  if (!me || m.who !== me) return;
  if (m.type === 'request') {
    const key = ownKey();
    if (key) channel.postMessage({ type: 'key', who: me, to: m.id, key });
  } else if (m.type === 'key' && waiting.has(m.to)) {
    waiting.get(m.to)(m.key);
  } else if (m.type === 'unlocked') {
    unlockedListeners.forEach((fn) => fn());
  }
};

const onGoodbye = (event) => {
  const m = event.data || {};
  if (!m.who || m.who !== lastSignedInAs()) return;
  if (m.type === 'signed_out') onSignedOut?.();
  else if (m.type === 'quick_exit') (onQuickExit || onSignedOut)?.();
};

const getChannel = () => {
  if (!channel && typeof BroadcastChannel !== 'undefined') {
    channel = new BroadcastChannel(CHANNEL);
    channel.onmessage = (event) => {
      const type = event.data?.type;
      if (type === 'signed_out' || type === 'quick_exit') onGoodbye(event);
      else onMessage(event);
    };
    signedInAs();
    // Signing in from another tab: remember who, for the goodbyes above.
    window.addEventListener?.('storage', (e) => {
      if (e.key === 'auth_token' && e.newValue) knownAs = subjectOf(e.newValue) || knownAs;
    });
  }
  return channel;
};

/** Start answering other tabs. Call once, as early as possible. */
export const startKeySharing = ({ signedOut, quickExit } = {}) => {
  onSignedOut = signedOut || null;
  onQuickExit = quickExit || null;
  getChannel();
};

/** Ask the other open tabs for the key. Resolves with it, or null. */
export const askOtherTabs = (timeoutMs = 400) => {
  const ch = getChannel();
  const who = signedInAs();
  if (!ch || !who) return Promise.resolve(null);
  const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return new Promise((resolve) => {
    const done = (key) => {
      waiting.delete(id);
      clearTimeout(timer);
      resolve(key || null);
    };
    const timer = setTimeout(() => done(null), timeoutMs);
    waiting.set(id, done);
    ch.postMessage({ type: 'request', who, id });
  });
};

export const announceUnlocked = () => {
  const who = signedInAs();
  if (who) getChannel()?.postMessage({ type: 'unlocked', who });
};

export const announceSignedOut = (who = signedInAs()) => {
  if (who) getChannel()?.postMessage({ type: 'signed_out', who });
};

export const announceQuickExit = (who = signedInAs()) => {
  if (who) getChannel()?.postMessage({ type: 'quick_exit', who });
};

/** Call `fn` whenever another tab unlocks. Returns an unsubscribe. */
export const onOtherTabUnlocked = (fn) => {
  unlockedListeners.add(fn);
  return () => unlockedListeners.delete(fn);
};
